import {
  fetchCircuits,
  fetchCircuitById,
  deleteCircuit,
  importCircuitsBackup,
  updateCircuitStatus,
  type SavedCircuit,
} from './api.js';
import { renderList, setListLoading, setListError } from './listView.js';
import { renderDetail, setDetailLoading, setDetailError } from './detailView.js';
import { mountCircuit3dEditorModal } from './circuit3dEditorModal.js';
import { createManualCircuitDraft } from './circuitEditor.js';
import { createPillButton } from '../ui/pillButton.js';
import { circuitDisplayTitle } from './circuitDisplay.js';
import { exportCircuitsToFile, readCircuitBackupFromFile } from './circuitBackup.js';
import { applyRoleVisibility } from '../auth/navAccount.js';
import { openConfirmModal } from '../ui/confirmModal.js';
import type { GaragePaneHandle, GaragePaneHosts } from '../garage/garageEmbed.js';
import { parseGarageNumericId } from '../garage/garageHash.js';

const SELECTED_ID_KEY = 'multirx-selected-circuit-id';

function createNewCircuitButton(onClick: () => void): HTMLElement {
  const newCircuitBtn = createPillButton('Nouveau', {
    variant: 'soft',
    small: true,
    class: 'circuits-new-circuit-btn',
    ariaLabel: 'Nouveau circuit',
    onClick,
  });
  newCircuitBtn.dataset.role = 'admin,moderator';
  return newCircuitBtn;
}

/** Montage embarqué dans le shell garage (sans tiroir mobile autonome). */
export function mountCircuitsPane(hosts: GaragePaneHosts): GaragePaneHandle {
  const { listHost, detailHost, actionsHost, modalRoot, initialId, onSelectId } = hosts;

  listHost.innerHTML = '';
  detailHost.innerHTML = '';
  actionsHost.innerHTML = '';

  const listContainer = document.createElement('div');
  listContainer.className = 'circuits-sidebar garage-sidebar';

  const listToolbar = document.createElement('div');
  listToolbar.className = 'garage-sidebar-toolbar-host';
  const toolbarInner = document.createElement('div');
  toolbarInner.className = 'garage-sidebar-toolbar';
  const search = document.createElement('input');
  search.type = 'search';
  search.className = 'garage-toolbar-search';
  search.placeholder = 'Rechercher un circuit…';
  search.setAttribute('aria-label', 'Filtrer les circuits');
  const countEl = document.createElement('p');
  countEl.className = 'garage-toolbar-count';
  countEl.setAttribute('aria-live', 'polite');
  toolbarInner.append(search, countEl);
  listToolbar.appendChild(toolbarInner);

  const listInner = document.createElement('div');
  listInner.className = 'circuits-sidebar-inner garage-sidebar-list';
  listContainer.append(listToolbar, listInner);
  listHost.appendChild(listContainer);

  const detailContainer = detailHost;
  detailContainer.classList.add('circuits-main');

  const importInput = document.createElement('input');
  importInput.type = 'file';
  importInput.accept = 'application/json,.json';
  importInput.hidden = true;
  modalRoot.appendChild(importInput);

  const edit3dModal = mountCircuit3dEditorModal(modalRoot, (updated) => {
    void loadList().then(() => selectCircuit(updated.id));
  });

  function openManual3dEditor(): void {
    edit3dModal.open(createManualCircuitDraft());
  }

  actionsHost.appendChild(createNewCircuitButton(() => openManual3dEditor()));
  applyRoleVisibility();

  let circuits: SavedCircuit[] = [];
  const fromStore = parseInt(sessionStorage.getItem(SELECTED_ID_KEY) ?? '', 10);
  let selectedId: number | null =
    parseGarageNumericId(initialId ?? null) ??
    (Number.isFinite(fromStore) && fromStore > 0 ? fromStore : null);

  function clearStoredSelection(): void {
    selectedId = null;
    sessionStorage.removeItem(SELECTED_ID_KEY);
    onSelectId?.(null);
  }

  function filteredCircuits(): SavedCircuit[] {
    const q = search.value.trim().toLowerCase();
    if (!q) return circuits;
    return circuits.filter((c) => circuitDisplayTitle(c).toLowerCase().includes(q));
  }

  function refreshListUi(): void {
    const filtered = filteredCircuits();
    countEl.textContent =
      filtered.length === circuits.length
        ? `${circuits.length} circuit${circuits.length !== 1 ? 's' : ''}`
        : `${filtered.length} / ${circuits.length} affichés`;
    renderList(listInner, filtered, (id) => selectCircuit(id), selectedId);
  }

  function loadList(): Promise<void> {
    setListLoading(listInner, true);
    return fetchCircuits()
      .then((data) => {
        circuits = data;
        refreshListUi();
      })
      .catch((err) => {
        setListError(listInner, err instanceof Error ? err.message : 'Erreur inconnue');
      });
  }

  function selectCircuit(id: number): void {
    selectedId = id;
    sessionStorage.setItem(SELECTED_ID_KEY, String(id));
    onSelectId?.(String(id));
    refreshListUi();
    loadDetail(id);
  }

  function detailHandlers(_circuit: SavedCircuit) {
    return {
      onImport: () => importInput.click(),
      onEdit3D: (c: SavedCircuit) => edit3dModal.open(c),
      onDelete: (c: SavedCircuit) => {
        const title = circuitDisplayTitle(c);
        const replayCount = c.replayCount ?? 0;
        const replayWarning =
          replayCount > 0
            ? ` Ses ${replayCount} replay${replayCount > 1 ? 's' : ''} associé${replayCount > 1 ? 's' : ''} seront aussi définitivement effacé${replayCount > 1 ? 's' : ''}.`
            : '';
        void openConfirmModal(modalRoot, {
          title: 'Supprimer le circuit',
          message: `Supprimer définitivement « ${title} » ?${replayWarning}\n\nCette action est irréversible. Pour retirer ce circuit de la rotation des saisons sans perdre son historique, préférez « Archiver ».`,
          confirmLabel: 'Supprimer',
          cancelLabel: 'Annuler',
          danger: true,
        }).then((confirmed) => {
          if (!confirmed) return;
          void deleteCircuit(c.id)
            .then(() => {
              circuits = circuits.filter((item) => item.id !== c.id);
              const wasSelected = selectedId === c.id;
              if (wasSelected) {
                selectedId = circuits[0]?.id ?? null;
                if (selectedId === null) {
                  clearStoredSelection();
                } else {
                  sessionStorage.setItem(SELECTED_ID_KEY, String(selectedId));
                  onSelectId?.(String(selectedId));
                }
              }
              refreshListUi();
              if (wasSelected) {
                if (selectedId === null) {
                  renderDetail(detailContainer, null, undefined);
                } else {
                  loadDetail(selectedId);
                }
              }
            })
            .catch((err) => {
              setDetailError(
                detailContainer,
                err instanceof Error ? err.message : 'Erreur lors de la suppression'
              );
            });
        });
      },
      onRenamed: (updated: SavedCircuit) => {
        circuits = circuits.map((c) => (c.id === updated.id ? updated : c));
        refreshListUi();
      },
      onExport: (c: SavedCircuit) => {
        exportCircuitsToFile([c]);
      },
      onApprove: (c: SavedCircuit) => {
        void updateCircuitStatus(c.id, 'published')
          .then((updated) => {
            circuits = circuits.map((item) => (item.id === updated.id ? updated : item));
            renderDetail(detailContainer, updated, detailHandlers(updated));
          })
          .catch((err) => window.alert(err instanceof Error ? err.message : 'Erreur inconnue'));
      },
      onReject: (c: SavedCircuit) => {
        void updateCircuitStatus(c.id, 'rejected')
          .then((updated) => {
            circuits = circuits.map((item) => (item.id === updated.id ? updated : item));
            renderDetail(detailContainer, updated, detailHandlers(updated));
          })
          .catch((err) => window.alert(err instanceof Error ? err.message : 'Erreur inconnue'));
      },
      onArchive: (c: SavedCircuit) => {
        void updateCircuitStatus(c.id, 'archived')
          .then((updated) => {
            circuits = circuits.map((item) => (item.id === updated.id ? updated : item));
            renderDetail(detailContainer, updated, detailHandlers(updated));
          })
          .catch((err) => window.alert(err instanceof Error ? err.message : 'Erreur inconnue'));
      },
      onUnarchive: (c: SavedCircuit) => {
        void updateCircuitStatus(c.id, 'published')
          .then((updated) => {
            circuits = circuits.map((item) => (item.id === updated.id ? updated : item));
            renderDetail(detailContainer, updated, detailHandlers(updated));
          })
          .catch((err) => window.alert(err instanceof Error ? err.message : 'Erreur inconnue'));
      },
    };
  }

  function loadDetail(id: number): void {
    setDetailLoading(detailContainer, true);
    fetchCircuitById(id)
      .then((saved) => {
        renderDetail(detailContainer, saved, detailHandlers(saved));
      })
      .catch((err) => {
        const message = err instanceof Error ? err.message : 'Erreur inconnue';
        if (message.includes('Circuit non trouvé')) {
          clearStoredSelection();
          refreshListUi();
          setDetailError(
            detailContainer,
            `Le circuit #${id} n’existe plus. Sélectionnez un autre circuit dans la liste.`
          );
          return;
        }
        setDetailError(detailContainer, message);
      });
  }

  search.addEventListener('input', () => refreshListUi());

  importInput.addEventListener('change', () => {
    const file = importInput.files?.[0];
    importInput.value = '';
    if (!file) return;
    void (async () => {
      try {
        const backup = await readCircuitBackupFromFile(file);
        const result = await importCircuitsBackup(backup);
        await loadList();
        const first = result.circuits[0];
        if (first?.id != null) {
          selectCircuit(first.id);
        }
        window.alert(`${result.imported} circuit(s) importé(s) avec succès.`);
      } catch (err) {
        window.alert(err instanceof Error ? err.message : 'Import impossible.');
      }
    })();
  });

  void loadList().then(() => {
    if (selectedId === null) {
      renderDetail(detailContainer, null, undefined);
      return;
    }
    if (circuits.some((c) => c.id === selectedId)) {
      selectCircuit(selectedId);
      return;
    }
    clearStoredSelection();
    renderDetail(detailContainer, null, undefined);
  });

  return {
    dispose() {
      listHost.innerHTML = '';
      detailHost.innerHTML = '';
      actionsHost.innerHTML = '';
      importInput.remove();
    },
  };
}

export function mountCircuitsApp(root: HTMLElement): void {
  // Sur mobile/tablette (voir `circuits.css`, `@media (max-width: 1023px)`), la liste
  // et le détail ne s'affichent plus jamais empilés en même temps (illisible en petit
  // écran) : un seul panneau visible à la fois, piloté par `layout.dataset.mobileView`
  // ("list" tant qu'aucun circuit n'est choisi, "detail" une fois un circuit ouvert).
  // Le retour à la liste passe par un tiroir plein écran (`is-drawer-open`) plutôt que
  // par un changement de panneau permanent, pour ne pas perdre le détail affiché.
  const drawerHeader = document.createElement('div');
  drawerHeader.className = 'circuits-drawer-header';
  drawerHeader.innerHTML = `<span class="circuits-drawer-title">Circuits</span>`;
  const drawerCloseBtn = createPillButton('Fermer', { variant: 'soft', small: true });
  drawerHeader.appendChild(drawerCloseBtn);

  const listContainer = document.createElement('div');
  listContainer.className = 'circuits-sidebar';
  listContainer.appendChild(drawerHeader);
  const listInner = document.createElement('div');
  listInner.className = 'circuits-sidebar-inner';
  listContainer.appendChild(listInner);

  const detailContainer = document.createElement('div');
  detailContainer.className = 'circuits-main';

  const mobileToolbar = document.createElement('div');
  mobileToolbar.className = 'circuits-mobile-toolbar';
  const changeCircuitBtn = createPillButton('Changer de circuit', {
    variant: 'soft',
    small: true,
  });
  mobileToolbar.appendChild(changeCircuitBtn);

  const mainCol = document.createElement('div');
  mainCol.className = 'circuits-main-col';
  mainCol.appendChild(mobileToolbar);
  mainCol.appendChild(detailContainer);

  const layout = document.createElement('div');
  layout.className = 'circuits-layout';
  layout.dataset.mobileView = 'list';
  layout.appendChild(listContainer);
  layout.appendChild(mainCol);

  function openDrawer(): void {
    layout.classList.add('is-drawer-open');
  }
  function closeDrawer(): void {
    layout.classList.remove('is-drawer-open');
  }
  changeCircuitBtn.addEventListener('click', openDrawer);
  drawerCloseBtn.addEventListener('click', closeDrawer);

  const header = document.createElement('header');
  header.className = 'circuits-header';
  header.innerHTML = `
    <nav aria-label="Fil d'Ariane"><ol class="breadcrumb" style="margin-bottom:0.5rem">
      <li><a href="#garage">Garage</a></li>
      <li>Circuits</li>
    </ol></nav>
    <div class="circuits-header-title-row">
      <h1 class="circuits-title">Circuits</h1>
      <div class="circuits-header-actions"></div>
    </div>
    <p class="circuits-subtitle">Consultez et sélectionnez vos tracés pour les courses</p>`;
  header.querySelector('.breadcrumb a')?.addEventListener('click', (e) => {
    e.preventDefault();
    window.location.hash = 'garage';
  });

  root.innerHTML = '';
  root.appendChild(header);

  const edit3dModal = mountCircuit3dEditorModal(root, (updated) => {
    void loadList().then(() => selectCircuit(updated.id));
  });

  const headerActions = header.querySelector('.circuits-header-actions');
  const importInput = document.createElement('input');
  importInput.type = 'file';
  importInput.accept = 'application/json,.json';
  importInput.hidden = true;
  importInput.addEventListener('change', () => {
    const file = importInput.files?.[0];
    importInput.value = '';
    if (!file) return;
    void (async () => {
      try {
        const backup = await readCircuitBackupFromFile(file);
        const result = await importCircuitsBackup(backup);
        await loadList();
        const first = result.circuits[0];
        if (first?.id != null) {
          selectCircuit(first.id);
        }
        window.alert(`${result.imported} circuit(s) importé(s) avec succès.`);
      } catch (err) {
        window.alert(err instanceof Error ? err.message : 'Import impossible.');
      }
    })();
  });
  root.appendChild(importInput);

  if (headerActions) {
    headerActions.appendChild(createNewCircuitButton(() => openManual3dEditor()));
  }
  applyRoleVisibility();

  root.appendChild(layout);

  let circuits: SavedCircuit[] = [];
  let selectedId: number | null =
    parseInt(sessionStorage.getItem(SELECTED_ID_KEY) ?? '', 10) || null;

  function clearStoredSelection(): void {
    selectedId = null;
    sessionStorage.removeItem(SELECTED_ID_KEY);
  }

  function loadList(): Promise<void> {
    setListLoading(listInner, true);
    return fetchCircuits()
      .then((data) => {
        circuits = data;
        renderList(listInner, data, (id) => selectCircuit(id), selectedId);
      })
      .catch((err) => {
        setListError(listInner, err instanceof Error ? err.message : 'Erreur inconnue');
      });
  }

  function syncMobileView(): void {
    layout.dataset.mobileView = selectedId === null ? 'list' : 'detail';
  }

  function selectCircuit(id: number): void {
    selectedId = id;
    sessionStorage.setItem(SELECTED_ID_KEY, String(id));
    listInner.querySelectorAll('.circuit-card').forEach((el) => {
      el.classList.toggle('circuit-card--selected', (el as HTMLElement).dataset.id === String(id));
    });
    syncMobileView();
    closeDrawer();
    loadDetail(id);
  }

  function detailHandlers(_circuit: SavedCircuit) {
    return {
      onImport: () => importInput.click(),
      onEdit3D: (c: SavedCircuit) => edit3dModal.open(c),
      onDelete: (c: SavedCircuit) => {
        const title = circuitDisplayTitle(c);
        const replayCount = c.replayCount ?? 0;
        const replayWarning =
          replayCount > 0
            ? ` Ses ${replayCount} replay${replayCount > 1 ? 's' : ''} associé${replayCount > 1 ? 's' : ''} seront aussi définitivement effacé${replayCount > 1 ? 's' : ''}.`
            : '';
        void openConfirmModal(root, {
          title: 'Supprimer le circuit',
          message: `Supprimer définitivement « ${title} » ?${replayWarning}\n\nCette action est irréversible. Pour retirer ce circuit de la rotation des saisons sans perdre son historique, préférez « Archiver ».`,
          confirmLabel: 'Supprimer',
          cancelLabel: 'Annuler',
          danger: true,
        }).then((confirmed) => {
          if (!confirmed) return;
          void deleteCircuit(c.id)
            .then(() => {
              circuits = circuits.filter((item) => item.id !== c.id);
              const wasSelected = selectedId === c.id;
              if (wasSelected) {
                selectedId = circuits[0]?.id ?? null;
                if (selectedId === null) {
                  clearStoredSelection();
                } else {
                  sessionStorage.setItem(SELECTED_ID_KEY, String(selectedId));
                }
              }
              renderList(listInner, circuits, (id) => selectCircuit(id), selectedId);
              if (wasSelected) {
                syncMobileView();
                if (selectedId === null) {
                  renderDetail(detailContainer, null, undefined);
                } else {
                  loadDetail(selectedId);
                }
              }
            })
            .catch((err) => {
              setDetailError(
                detailContainer,
                err instanceof Error ? err.message : 'Erreur lors de la suppression'
              );
            });
        });
      },
      onRenamed: (updated: SavedCircuit) => {
        circuits = circuits.map((c) => (c.id === updated.id ? updated : c));
        renderList(listInner, circuits, (id) => selectCircuit(id), selectedId);
      },
      onExport: (c: SavedCircuit) => {
        exportCircuitsToFile([c]);
      },
      onApprove: (c: SavedCircuit) => {
        void updateCircuitStatus(c.id, 'published')
          .then((updated) => {
            circuits = circuits.map((item) => (item.id === updated.id ? updated : item));
            renderDetail(detailContainer, updated, detailHandlers(updated));
          })
          .catch((err) => window.alert(err instanceof Error ? err.message : 'Erreur inconnue'));
      },
      onReject: (c: SavedCircuit) => {
        void updateCircuitStatus(c.id, 'rejected')
          .then((updated) => {
            circuits = circuits.map((item) => (item.id === updated.id ? updated : item));
            renderDetail(detailContainer, updated, detailHandlers(updated));
          })
          .catch((err) => window.alert(err instanceof Error ? err.message : 'Erreur inconnue'));
      },
      onArchive: (c: SavedCircuit) => {
        void updateCircuitStatus(c.id, 'archived')
          .then((updated) => {
            circuits = circuits.map((item) => (item.id === updated.id ? updated : item));
            renderDetail(detailContainer, updated, detailHandlers(updated));
          })
          .catch((err) => window.alert(err instanceof Error ? err.message : 'Erreur inconnue'));
      },
      onUnarchive: (c: SavedCircuit) => {
        void updateCircuitStatus(c.id, 'published')
          .then((updated) => {
            circuits = circuits.map((item) => (item.id === updated.id ? updated : item));
            renderDetail(detailContainer, updated, detailHandlers(updated));
          })
          .catch((err) => window.alert(err instanceof Error ? err.message : 'Erreur inconnue'));
      },
    };
  }

  function loadDetail(id: number): void {
    setDetailLoading(detailContainer, true);
    fetchCircuitById(id)
      .then((saved) => {
        renderDetail(detailContainer, saved, detailHandlers(saved));
      })
      .catch((err) => {
        const message = err instanceof Error ? err.message : 'Erreur inconnue';
        if (message.includes('Circuit non trouvé')) {
          clearStoredSelection();
          syncMobileView();
          renderList(listInner, circuits, (cid) => selectCircuit(cid), null);
          setDetailError(
            detailContainer,
            `Le circuit #${id} n’existe plus. Sélectionnez un autre circuit dans la liste.`
          );
          return;
        }
        setDetailError(detailContainer, message);
      });
  }

  /** Ouvre directement l'éditeur 3D sur une grille vide — pour tracer un
   * circuit de zéro. Rien n'est enregistré tant que l'utilisateur ne clique
   * pas "Enregistrer" dans l'éditeur. */
  function openManual3dEditor(): void {
    edit3dModal.open(createManualCircuitDraft());
  }

  void loadList().then(() => {
    if (selectedId === null) {
      syncMobileView();
      renderDetail(detailContainer, null, undefined);
      return;
    }
    if (circuits.some((c) => c.id === selectedId)) {
      selectCircuit(selectedId);
      return;
    }
    clearStoredSelection();
    syncMobileView();
    renderDetail(detailContainer, null, undefined);
  });
}
